import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeroSection from "../components/HeroSection";
import { get } from "../api/config";

const OngoingProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOngoingProjects();
  }, []);

  const fetchOngoingProjects = async () => {
    try {
      setLoading(true);
      const response = await get("public/projects?status_id=2");

      if (response.data && Array.isArray(response.data)) {
        setProjects(response.data);
      } else {
        setProjects([]);
      }
    } catch (err) {
      setError("Failed to fetch ongoing projects");
      console.error("Error fetching projects:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full bg-white py-12 px-4">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 inline-block relative pb-2">
              Ongoing Projects
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600"></div>
            </h2>
          </div>
          <div className="text-center py-8">
            <div className="text-gray-600">Loading ongoing projects...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white py-12 px-4">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 inline-block relative pb-2">
              Ongoing Projects
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600"></div>
            </h2>
          </div>
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">{error}</div>
            <button
              onClick={fetchOngoingProjects}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white py-12 px-4">
      {/* Header */}
      <HeroSection />

      <div className="mx-auto max-w-6xl px-4">
        {/* Meta row */}
        <div className="flex items-center justify-between text-sm text-gray-600 py-3">
          {/* You can add page info or other meta data here */}
        </div>

        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-green-600 inline-block relative pb-2">
            Ongoing Projects
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600"></div>
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            Currently active infrastructure projects that are making progress in
            our community.
          </p>
        </div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">
              No ongoing projects found
            </div>
            <p className="text-gray-400 mt-2">
              There are currently no projects in progress.
            </p>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {projects.map((project) => (
              <ProjectCard key={project.project_id} project={project} />
            ))}
          </div>
        )}

        {/* Project Count */}
        {projects.length > 0 && (
          <div className="text-center mb-8">
            <div className="text-gray-600">
              Showing {projects.length} ongoing project
              {projects.length !== 1 ? "s" : ""}
            </div>
          </div>
        )}

        {/* View More Button */}
        {projects.length > 0 && (
          <div className="text-center">
            <button className="text-gray-900 text-lg font-semibold underline hover:text-green-600 transition-colors duration-300">
              View More
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Project Card Component
const ProjectCard = ({ project }) => {
  const navigate = useNavigate();
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setIsAnimating(true);

    const progress = parseInt(project.progress) || 0;
    const duration = 2000;
    const steps = 100;
    const increment = progress / steps;
    const stepTime = duration / steps;

    let currentProgress = 0;
    const timer = setInterval(() => {
      currentProgress += increment;
      if (currentProgress >= progress) {
        currentProgress = progress;
        clearInterval(timer);
        setIsAnimating(false);
      }
      setAnimatedProgress(Math.floor(currentProgress));
    }, stepTime);

    return () => clearInterval(timer);
  }, [project.progress]);

  const handleCardClick = () => {
    navigate(`/projects/${project.project_id}`);
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return "bg-green-500";
    if (progress >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getRunningEmoji = () => {
    return isAnimating ? "🏃‍♂️‍➡️" : "🏃‍♂️‍➡️";
  };

  return (
    <div
      className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      onClick={handleCardClick}
    >
      {/* Image Container */}
      <div className="relative h-48">
        <img
          src={getProjectImage(project)}
          alt={project.project_name}
          className="w-full h-full object-cover"
        />
        {/* Progress Badge with Animated Emoji */}
        <div className="absolute bottom-3 left-3 bg-green-600 text-white px-3 py-1 rounded-full flex items-center gap-2 text-sm font-bold shadow-lg">
          <span className={`${isAnimating ? "animate-bounce" : ""}`}>
            {getRunningEmoji()}
          </span>
          {animatedProgress}%
        </div>

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center">
          <div className="bg-white bg-opacity-90 rounded-full p-3 transform scale-0 hover:scale-100 transition-transform duration-300">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 bg-white">
        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 leading-tight">
          {formatProjectTitle(project)}
        </h3>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {project.location || "Location not specified"}
        </p>

        {/* Progress Bar with Running Emoji */}
        <div className="mb-4">
          <div className="flex justify-between items-center text-xs text-gray-600 mb-2">
            <span>Progress</span>
            <span className="font-semibold">{animatedProgress}% Complete</span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-3 relative">
            {/* Progress Fill */}
            <div
              className={`h-3 rounded-full ${getProgressColor(
                animatedProgress
              )} transition-all duration-300 ease-out relative`}
              style={{ width: `${animatedProgress}%` }}
            >
              {/* Running Emoji on Progress Bar */}
              <div
                className="absolute right-0 top-1/2 transform -translate-y-1/2 text-lg transition-all duration-300"
                style={{
                  marginRight: "-8px",
                  animation: isAnimating
                    ? "run 0.5s infinite alternate"
                    : "none",
                }}
              >
                🏃‍♂️‍➡️
              </div>
            </div>
          </div>
        </div>

        {/* Project Details */}
        <div className="flex justify-between items-center text-xs text-gray-500 mb-3">
          {project.start_date && (
            <div className="flex items-center gap-1">
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>{formatDate(project.start_date)}</span>
            </div>
          )}

          {project.estimated_cost && (
            <div className="text-right font-medium text-green-600">
              {formatCost(project.estimated_cost)}
            </div>
          )}
        </div>

        {/* Status Badge */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white text-center py-2.5 font-bold text-sm rounded-lg shadow-sm">
          {project.status?.status_name || "Ongoing"}
        </div>

        {/* Click Hint */}
        <div className="text-center mt-2">
          <span className="text-xs text-gray-400">Click to view details</span>
        </div>
      </div>

      {/* CSS animations */}
      <style jsx>{`
        @keyframes run {
          0% {
            transform: translateY(-50%) rotate(0deg);
          }
          100% {
            transform: translateY(-50%) rotate(5deg);
          }
        }
      `}</style>
    </div>
  );
};

// Helper functions
const formatProjectTitle = (project) => {
  if (project.length_size && project.project_name) {
    return `${project.length_size} ${project.project_name}`;
  }
  return project.project_name || "Unnamed Project";
};

const getProjectImage = (project) => {
  const projectName = project.project_name?.toLowerCase() || "";

  if (projectName.includes("drain")) {
    return "https://images.unsplash.com/photo-1581094794329-c8112c4e5190?w=400&h=300&fit=crop";
  } else if (projectName.includes("road")) {
    return "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=400&h=300&fit=crop";
  } else {
    return "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop";
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatCost = (cost) => {
  if (!cost) return "₹ 0";
  const amount = parseFloat(cost);

  if (amount >= 10000000) {
    return `₹ ${(amount / 10000000).toFixed(1)} Cr`;
  } else if (amount >= 100000) {
    return `₹ ${(amount / 100000).toFixed(1)} L`;
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

export default OngoingProjects;
